import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Cloud } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);

  const validatePassword = (pwd) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    const errors = [];
    if (pwd.length < minLength) errors.push('at least 8 characters');
    if (!hasUpperCase) errors.push('one uppercase letter');
    if (!hasLowerCase) errors.push('one lowercase letter');
    if (!hasNumber) errors.push('one number');
    if (!hasSpecialChar) errors.push('one special character (!@#$%^&* etc.)');

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      toast.error(`Password must contain ${passwordErrors.join(', ')}`);
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name,
        email,
        password,
        org_name: orgName
      });
      
      // Redirect to signup success page
      navigate('/signup-success', { state: { email } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-4">
                <Cloud className="text-white" size={32} />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('auth.getStarted')}</h1>
              <p className="text-slate-600">{t('auth.createAccount')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="name">{t('auth.yourName')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('placeholder.enterName')}
                  required
                  data-testid="signup-name-input"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="orgName">{t('auth.orgName')}</Label>
                <Input
                  id="orgName"
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t('form.organizationName')}
                  required
                  data-testid="signup-org-input"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="email">{t('form.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  data-testid="signup-email-input"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="password">{t('form.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="signup-password-input"
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-500 mt-1.5">
                  {t('auth.passwordRules')}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                disabled={loading}
                data-testid="signup-submit-button"
              >
                {loading ? t('common.loading') : t('btn.signup')}
              </Button>
            </form>

            <p className="text-center mt-6 text-sm text-slate-600">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="login-link">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-cyan-600 via-blue-600 to-blue-700 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold mb-6">Join Keyless PMS</h2>
          <p className="text-xl text-cyan-50 mb-8">
            Start managing your properties with our modern, cloud-native PMS. No credit card required to get started.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
